import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  ChevronDown,
  ChevronRight,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Atom,
  FlaskConical,
  Beaker,
  TestTube,
  Flame,
  Battery,
  Plus,
  Eye,
  EyeOff,
  PenTool,
  List,
  Sparkles,
  FileQuestion
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';

import { 
  chemistryCurriculum, 
  chemistrySchemeOfWork,
  chemistryLessonPlans
} from '@/data/chemistryCurriculum';
import { 
  year11ChemistryUnitTests,
  year12ChemistryUnitTests,
  year13ChemistryUnitTests,
  year11ChemistryExercises,
  year12ChemistryExercises,
  year13ChemistryExercises,
  chemistryResources,
  chemistryTestBlueprints,
  year12ChemistryTestBlueprints,
  year13ChemistryTestBlueprints,
  chemistryAssessment
} from '@/data/chemistryUnitTests';
import { chemistryWeeklyLessons } from '@/data/chemistryWeeklyLessons';
import PrintableWorksheet from '@/components/curriculum/PrintableWorksheet';
import ChemistrySchemeOfWork from '@/components/curriculum/ChemistrySchemeOfWork';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { Files } from 'lucide-react';
import { getCurrentUser } from '@/data/users';

// Student Notes Data
import { 
  year11ChemistryStudentNotes, 
  year12ChemistryStudentNotes, 
  year13ChemistryStudentNotes 
} from '@/data/studentNotes';
import apiService from '@/services/api';

export default function ChemistryCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [expandedStrands, setExpandedStrands] = useState({});
  const [showAnswers, setShowAnswers] = useState({});
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  // Fetch teacher's assigned classes to get their year levels
  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        // Extract unique years from teacher's classes (Chemistry is Years 11-13)
        const years = [...new Set(classes
          .filter(c => c.year >= 11 && c.year <= 13)
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years);
        // Auto-select first year if available
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        // Fallback to all years if API fails
        setTeacherYears(["Year 11", "Year 12", "Year 13"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  // Get unit tests for the selected year
  const getUnitTests = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const testsMap = {
      'year11': year11ChemistryUnitTests,
      'year12': year12ChemistryUnitTests,
      'year13': year13ChemistryUnitTests
    };
    return testsMap[yearKey] || year11ChemistryUnitTests;
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getUnitTests();
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests?.[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  const strandColors = ["blue", "green", "purple", "orange", "red"];
  const strandIcons = {
    0: Atom,        // Atomic Structure
    1: Beaker,      // Quantitative Chemistry
    2: Flame,       // Physical Chemistry
    3: Battery,     // Oxidation & Reduction
    4: TestTube     // Organic Chemistry
  };

  // Toggle show/hide answers for tests
  const toggleAnswers = (testId) => {
    setShowAnswers(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  // Print test
  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #1e40af; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #1e40af; background: #eff6ff; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #1e40af; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .question { margin-bottom: 15px; padding: 10px; border-bottom: 1px solid #eee; }
          .q-number { font-weight: bold; color: #1e40af; }
          .options { margin-left: 25px; margin-top: 8px; }
          .option { margin: 5px 0; }
          .marks { float: right; color: #666; font-style: italic; }
          .answer-lines { margin-top: 10px; }
          .answer-line { border-bottom: 1px solid #ccc; height: 25px; margin: 8px 0; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Chemistry - St Patrick's College</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        <div class="instructions">
          <strong>Instructions:</strong>
          <ul>${test.instructions?.map(i => `<li>${i}</li>`).join('') || ''}</ul>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            ${section.questions?.map(q => `
              <div class="question">
                <span class="q-number">Q${q.id}.</span> ${q.question} <span class="marks">[${q.marks} mark${q.marks > 1 ? 's' : ''}]</span>
                ${q.options ? `<div class="options">${q.options.map(opt => `<div class="option">${opt}</div>`).join('')}</div>` : ''}
                ${!q.options ? `<div class="answer-lines">${Array(Math.ceil(q.marks / 2)).fill('<div class="answer-line"></div>').join('')}</div>` : ''}
              </div>
            `).join('') || ''}
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Create assessment from curriculum
  const handleCreateAssessment = (test, term) => {
    const assessmentData = {
      fromCurriculum: true,
      title: test.title,
      subject: 'Chemistry',
      year: selectedYear,
      term: term,
      totalMarks: test.totalMarks,
      duration: test.duration,
      instructions: test.instructions,
      sections: test.sections,
      testId: test.id,
      autoSync: true,
      syncConfig: {
        assessmentType: 'test',
        weightPercentage: test.totalMarks >= 40 ? 20 : 15,
        maxMarks: test.totalMarks,
        markBreakdown: test.sections?.map(s => ({
          section: s.name,
          marks: s.marks,
          questionCount: s.questions?.length || 0
        })) || [],
        answerKey: extractAnswerKey(test),
        rubric: {
          totalMarks: test.totalMarks,
          sections: test.sections?.map(s => ({
            name: s.name,
            marks: s.marks,
            percentage: Math.round((s.marks / test.totalMarks) * 100)
          })) || [],
          gradeScale: {
            A: { min: 80, max: 100 },
            B: { min: 60, max: 79 },
            C: { min: 50, max: 59 },
            D: { min: 40, max: 49 },
            F: { min: 0, max: 39 }
          }
        }
      },
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.name || 'Teacher'
    };

    localStorage.setItem('pendingCurriculumAssessment', JSON.stringify(assessmentData));

    toast.success('Redirecting to Grade Book to create assessment...', {
      description: `${test.title} will be added to Grade Book with auto-sync enabled.`
    });
    navigate('/dashboard/gradebook', { state: { assessmentFromCurriculum: assessmentData } });
  };

  // Extract answer key from test
  const extractAnswerKey = (test) => {
    const answerKey = {};
    test.sections?.forEach(section => {
      section.questions?.forEach(q => {
        if (q.answer) {
          answerKey[`q${q.id}`] = q.answer;
        }
      });
    });
    return answerKey;
  };

  const toggleStrandExpand = (strandId) => {
    setExpandedStrands(prev => ({
      ...prev,
      [strandId]: !prev[strandId]
    }));
  };

  const currentYearData = chemistryCurriculum[selectedYear];
  const currentScheme = chemistrySchemeOfWork[selectedYear];
  const currentLessonPlans = chemistryLessonPlans[selectedYear] || [];

  const printSchemeOfWork = () => {
    const printWindow = window.open('', '_blank');
    const termKey = selectedTerm;
    const termData = currentScheme?.[termKey] || [];
    
    let content = `
      <html>
      <head>
        <title>Scheme of Work - ${selectedYear} Chemistry - ${termKey.replace('term', 'Term ')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #1e40af; }
          h2 { color: #374151; border-bottom: 2px solid #1e40af; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #1e40af; color: white; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>St Patrick's College</h1>
        <h2>${selectedYear} Chemistry - Scheme of Work - ${termKey.replace('term', 'Term ')}</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Topic</th>
              <th>Learning Outcomes</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            ${termData.map(item => `
              <tr>
                <td>${item.week}</td>
                <td>${item.topic}</td>
                <td>${item.outcomes}</td>
                <td>${item.activities}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printLessonPlan = (lesson) => {
    const printWindow = window.open('', '_blank');
    
    let content = `
      <html>
      <head>
        <title>Lesson Plan - ${lesson.topic}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
          h1 { text-align: center; color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
          .header { background: #f0f9ff; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .section { margin-bottom: 20px; }
          .section-title { background: #1e40af; color: white; padding: 8px 15px; font-weight: bold; }
          .section-content { padding: 15px; border: 1px solid #ddd; border-top: none; }
          ul { margin: 10px 0; padding-left: 20px; }
          .activity { margin: 10px 0; padding: 10px; background: #f8fafc; border-left: 3px solid #1e40af; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Lesson Plan: ${lesson.topic}</h1>
        <div class="header">
          <p><strong>Subject:</strong> Chemistry | <strong>Year:</strong> ${selectedYear} | <strong>Week:</strong> ${lesson.week}</p>
          <p><strong>Duration:</strong> ${lesson.duration}</p>
        </div>
        
        <div class="section">
          <div class="section-title">Learning Objectives</div>
          <div class="section-content">
            <ul>${lesson.objectives.map(obj => `<li>${obj}</li>`).join('')}</ul>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Materials Required</div>
          <div class="section-content">
            <ul>${lesson.materials.map(mat => `<li>${mat}</li>`).join('')}</ul>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Introduction (${lesson.introduction.time})</div>
          <div class="section-content">
            <p>${lesson.introduction.activity}</p>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Development Activities</div>
          <div class="section-content">
            ${lesson.development.map(dev => `
              <div class="activity">
                <strong>${dev.time}:</strong> ${dev.activity}
              </div>
            `).join('')}
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Conclusion (${lesson.conclusion.time})</div>
          <div class="section-content">
            <p>${lesson.conclusion.activity}</p>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Assessment</div>
          <div class="section-content">
            <p>${lesson.assessment}</p>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Homework</div>
          <div class="section-content">
            <p>${lesson.homework}</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <div className="space-y-6" data-testid="chemistry-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FlaskConical className="h-8 w-8 text-blue-600" />
            Chemistry Curriculum
          </h1>
          <p className="text-gray-600 mt-1">
            Vanuatu National Chemistry Syllabus - Years 11, 12 & 13
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select 
            value={selectedYear} 
            onValueChange={(value) => {
              console.log('Chemistry Year changed to:', value);
              setSelectedYear(value);
              // Reset tab to curriculum when year changes
              setActiveTab("curriculum");
            }}
          >
            <SelectTrigger className="w-32" data-testid="chemistry-year-selector">
              <SelectValue>{selectedYear}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(teacherYears.length > 0 ? teacherYears : ["Year 11", "Year 12", "Year 13"]).map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            5 Strands
          </Badge>
          <Badge variant="outline" className="text-green-600 border-green-600">
            40% IA / 60% Exam
          </Badge>
        </div>
      </div>

      {/* Course Overview Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">5</p>
              <p className="text-sm text-gray-600">Strands</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{currentYearData?.strands?.length * 10 || 50}+</p>
              <p className="text-sm text-gray-600">Learning Outcomes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">4</p>
              <p className="text-sm text-gray-600">Terms</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">40%</p>
              <p className="text-sm text-gray-600">Internal Assessment</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">60%</p>
              <p className="text-sm text-gray-600">External Exam</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs - Unit Tests, Exercises, and Notes are now in Student Resources */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="chem-curriculum-tab">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">Curriculum</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="chem-schemes-tab">
            <List className="w-4 h-4" />
            <span className="hidden md:inline">Schemes</span>
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-purple-100" data-testid="chem-lesson-plans-tab">
            <PenTool className="w-4 h-4" />
            <span className="hidden md:inline">Lesson Plans</span>
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="chem-term-exam-tab">
            <FileQuestion className="w-4 h-4" />
            <span className="hidden md:inline">Term Exam</span>
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="chem-quick-print-tab">
            <Files className="w-4 h-4" />
            <span className="hidden md:inline">Quick Print</span>
          </TabsTrigger>
          <TabsTrigger value="ai-generator" className="flex items-center gap-2 bg-amber-100" data-testid="chem-ai-generator-tab">
            <Sparkles className="w-4 h-4" />
            <span className="hidden md:inline">AI Generator</span>
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2" data-testid="chem-resources-tab">
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">Resources</span>
          </TabsTrigger>
        </TabsList>

        {/* Lesson Plans Tab - Full Week Mon-Fri */}
        <TabsContent value="lesson-plans" data-testid="chem-lesson-plans-content">
          <LessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            subject="Chemistry"
            subjectColor="purple"
            allowedYears={['11', '12', '13']}
            weeklyLessons={chemistryWeeklyLessons}
          />
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" data-testid="chem-term-exam-content">
          <TermExamGenerator
            subject="Chemistry"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={getUnitTestsForYear()}
            subjectColor="purple"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" data-testid="chem-quick-print-content">
          <QuickPrintAll
            subject="Chemistry"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={chemistryWeeklyLessons}
            subjectColor="purple"
          />
        </TabsContent>

        {/* AI Generator Tab */}
        <TabsContent value="ai-generator" data-testid="chem-ai-generator-content">
          <AILessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            defaultSubject="chemistry" 
            schoolType="secondary"
            allowedYears={teacherYears}
            allowedSubjects={['chemistry']}
          />
        </TabsContent>

        {/* Schemes Tab - New comprehensive scheme with daily lesson plans */}
        <TabsContent value="schemes">
          <ChemistrySchemeOfWork year={parseInt(selectedYear.replace('Year ', ''))} />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                  {selectedYear} Chemistry - Strands & Learning Outcomes
                </CardTitle>
                <CardDescription>
                  Click on each strand to view detailed learning outcomes and topics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-3">
                  {currentYearData?.strands?.map((strand, index) => {
                    const IconComponent = strandIcons[index] || Atom;
                    const color = strandColors[index] || "blue";
                    
                    return (
                      <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg">
                        <AccordionTrigger className="px-4 hover:no-underline">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                              <IconComponent className={`h-5 w-5 text-${color}-600`} />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold">{strand.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {strand.outcomes?.length || 0} learning outcomes
                                {strand.weighting && ` • ${strand.weighting}% weighting`}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          {/* Topics */}
                          {strand.topics && strand.topics.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Topics:</h4>
                              <div className="grid gap-2">
                                {strand.topics.map((topic, idx) => (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                                    <Badge variant="outline" className="shrink-0">
                                      Week {topic.weeks}
                                    </Badge>
                                    <div>
                                      <p className="font-medium text-sm">{topic.name}</p>
                                      <p className="text-xs text-muted-foreground">{topic.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Learning Outcomes */}
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2">Learning Outcomes:</h4>
                            <ul className="space-y-2">
                              {strand.outcomes?.map((outcome, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm">
                                  <CheckCircle2 className={`h-4 w-4 text-${color}-500 shrink-0 mt-0.5`} />
                                  <span>{outcome}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Unit Tests, Exercises, and Student Notes tabs have been moved to Student Resources page */}

        {/* Resources Tab */}
        <TabsContent value="resources" data-testid="chem-resources-content">
          <div className="space-y-6">
            {/* Teaching Resources Grid */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Teaching Resources
                </CardTitle>
                <CardDescription>Reference materials, templates, and guides for Chemistry</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(chemistryResources || []).map((resource, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:border-blue-300 hover:shadow-sm transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-blue-800">{resource.title}</h4>
                        <Badge variant="outline" className="text-blue-600 text-xs">{resource.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <Badge className="bg-gray-100 text-gray-600 text-xs">{resource.unit}</Badge>
                          {resource.year && <Badge className="bg-blue-50 text-blue-600 text-xs">{resource.year}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="text-blue-600 hover:text-blue-700">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            onClick={() => {
                              setActiveTab('ai-generator');
                              toast.info(`Generate custom materials for: ${resource.title}`);
                            }}
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            AI Generate
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Assessment Structure */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Assessment Structure - {selectedYear}
                </CardTitle>
                <CardDescription>Overview of internal and external assessment requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Internal Assessment */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Internal Assessment ({chemistryAssessment?.year11?.internalAssessment?.weight || 40}%)
                    </h4>
                    <div className="space-y-2">
                      {chemistryAssessment?.year11?.internalAssessment?.components?.map((comp, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span>{comp.name}</span>
                          <div className="flex gap-2">
                            <Badge variant="outline">{comp.weight}%</Badge>
                            <Badge className="bg-green-100 text-green-700">{comp.count} tasks</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* External Examination */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      External Examination ({chemistryAssessment?.year11?.externalExamination?.weight || 60}%)
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Format:</span> {chemistryAssessment?.year11?.externalExamination?.format}</p>
                      <p><span className="font-medium">Sections:</span> {chemistryAssessment?.year11?.externalExamination?.sections}</p>
                      <div className="mt-3">
                        <p className="font-medium mb-2">Strand Weights:</p>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Atomic Structure & Bonding</span>
                            <Badge>{chemistryAssessment?.year11?.externalExamination?.strandWeights?.strand1}%</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Quantitative Chemistry</span>
                            <Badge>{chemistryAssessment?.year11?.externalExamination?.strandWeights?.strand2}%</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Physical Chemistry</span>
                            <Badge>{chemistryAssessment?.year11?.externalExamination?.strandWeights?.strand3}%</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Oxidation & Reduction</span>
                            <Badge>{chemistryAssessment?.year11?.externalExamination?.strandWeights?.strand4}%</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skill Levels */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3">Assessment Skill Levels</h4>
                  <div className="grid md:grid-cols-4 gap-3">
                    {chemistryAssessment?.year11?.skillLevels && Object.entries(chemistryAssessment.year11.skillLevels).map(([level, description], idx) => (
                      <div key={level} className="p-3 border rounded-lg text-center">
                        <Badge className={`mb-2 ${
                          idx === 0 ? 'bg-green-100 text-green-700' :
                          idx === 1 ? 'bg-blue-100 text-blue-700' :
                          idx === 2 ? 'bg-purple-100 text-purple-700' :
                          'bg-rose-100 text-rose-700'
                        }`}>{level.charAt(0).toUpperCase() + level.slice(1)}</Badge>
                        <p className="text-xs text-gray-600">{description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
